import { z } from 'zod';
import { idSchema } from '../../ontology/primitives.ts';
import { instant } from './primitives.ts';

/**
 * Restates `SessionVerdict`: the wire may not import the broker (it pulls in the database).
 * `readBrokerStatus` assigns one to the other, so a new verdict fails to compile.
 */
const BROKER_SESSIONS = ['ok', 'ended', 'unreachable', 'logged-out'] as const;

const BROKER_REFUSALS = [
  'unavailable',
  'no-member',
  'not-linked',
  'not-opted-in',
  'logged-out',
  'trading-disabled',
] as const;

/** Re-read on every load: Zerodha flushes tokens at 6 AM IST, and any Kite login ends the API session. */
export const brokerStatusSchema = z.object({
  available: z.boolean(),
  linked: z.boolean(),
  kiteUserId: z.string().nullable(),
  session: z.enum(BROKER_SESSIONS),
  expiresAt: instant.nullable(),
  placing: z.boolean(),
  refusal: z.enum(BROKER_REFUSALS).nullable(),
  /** While the desk makes their app; `failed`: its last try did not. */
  asked: z.object({ zerodhaId: z.string(), failed: z.boolean() }).nullable(),
});

export type BrokerStatus = z.infer<typeof brokerStatusSchema>;

/** Use only to withhold an order offer, never to authorise one; the port decides. */
export const brokerWillPlace = (status: BrokerStatus): boolean =>
  status.available && status.linked && status.placing && status.refusal === null;

const wireFallbackSchema = z.object({
  positionId: idSchema,
  standing: z.boolean(),
});

export type WireFallback = z.infer<typeof wireFallbackSchema>;

export const standingFallbacksSchema = z.object({
  /** False: Zerodha was not asked, so `fallbacks` means nothing — not "none standing". */
  readable: z.boolean(),
  fallbacks: z.array(wireFallbackSchema),
  asOf: instant,
});

export type StandingFallbacks = z.infer<typeof standingFallbacksSchema>;
